<template>
  <div class="animated-button">
    <div class="animated-button__button-ring" :class="{ 'ring-animation': animate }" @animationend="stopAnimation" />

    <button class="animated-button__action-button" :class="{ 'dot-animation': animate }" v-bind="$attrs">
      <span class="animated-button__icon-background" />
      <InfoIconFilled class="animated-button__icon" />
    </button>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import InfoIconFilled from '@components/Icons/InfoIconFilled.vue';

const AnimationInterval = 1800000; // 30 minutes

export default defineComponent({
  name: 'AnimatedButton',

  inheritAttrs: false,

  components: {
    InfoIconFilled,
  },

  data() {
    return {
      animate: false,
      timerId: null,
    };
  },

  methods: {
    triggerAnimation() {
      this.animate = true;
    },

    stopAnimation() {
      this.animate = false;
    },

    startAnimationTimer() {
      this.timerId = setInterval(() => {
        this.triggerAnimation();
      }, AnimationInterval);
    },
  },

  mounted() {
    this.startAnimationTimer();
  },

  beforeUnmount() {
    this.stopAnimation();
    clearInterval(this.timerId);
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';

$animation-iteration-count: 5;
$animation-duration: 1.25s;
$animated-button-width: 40px;
$animated-button-height: 40px;

.animated-button {
  width: $animated-button-width;
  height: $animated-button-height;

  position: relative;
}

.animated-button__button-ring {
  pointer-events: none;
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 50%;
  left: -20px;
  top: -20px;
  opacity: 0;
  background-color: colors.$color-grey-blue-100;
}

.ring-animation {
  animation: pulse-ring $animation-duration cubic-bezier(0.215, 0.61, 0.355, 1) 0.8s $animation-iteration-count;
}

.animated-button__action-button {
  pointer-events: auto;

  position: absolute;
  left: 0;
  top: 0;
  width: $animated-button-width;
  height: $animated-button-height;
  border: none;
  transition: all 0.1s ease-in-out;

  color: colors.$color-blue;

  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: colors.$color-blue-tinted;
  }

  &:focus {
    .animated-button__icon {
      box-shadow: settings.$box-shadow-focus;
    }
  }
}

// white background needs to be a bit smaller to avoid
// flickering around the icon edges
.animated-button__icon-background {
  position: absolute;
  width: 22px;
  height: 22px;
  top: 9px;
  left: 9px;
  background: colors.$color-white;
  border-radius: 50%;
}

.animated-button__icon {
  position: relative;
  border-radius: 50%;
}

.dot-animation {
  animation: pulse-dot $animation-duration cubic-bezier(0.455, 0.03, 0.515, 0.955) $animation-iteration-count;
}

@keyframes pulse-ring {
  0% {
    opacity: 1;
    transform: scale(0.33);
  }
  80%,
  100% {
    opacity: 0;
  }
}

@keyframes pulse-dot {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(0.8);
  }
  100% {
    transform: scale(1);
  }
}
</style>
